import { Star } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { formatCurrency, getImageWithFallback } from "@/utilities"
import type { WooCommerceProduct } from "~/services/woo-commerce-service"

const PLACEHOLDER_IMAGE = "/static/img/not-found.svg"
const DEFAULT_PRODUCT_NAME = "Produto sem nome"
const INSTALLMENTS_COUNT = 10

interface ProductProps {
  product: WooCommerceProduct
}

export const ProductCard = ({ product }: ProductProps): React.ReactNode => {
  if (!product || product.stock_status === "outofstock") {
    return null
  }

  const productName = product.name || DEFAULT_PRODUCT_NAME
  const productLink = `/produtos/${product.slug}`

  const regularPrice = Number.parseFloat(product.regular_price) || 0
  const salePrice = product.sale_price
    ? Number.parseFloat(product.sale_price)
    : null

  const imageUrl = getImageWithFallback(
    product.images?.[0]?.src ?? PLACEHOLDER_IMAGE
  )

  const hasDiscount = salePrice && salePrice < regularPrice
  const displayPrice = hasDiscount ? salePrice : regularPrice

  const discountPercentage = hasDiscount
    ? Math.round(((regularPrice - salePrice) / regularPrice) * 100)
    : 0

  const rating = Number.parseFloat(product.average_rating) || 0
  const ratingCount = product.rating_count || 0
  const hasRating = ratingCount > 0

  return (
    <Link className='group block h-100' href={productLink} prefetch={false}>
      <article className='flex h-full flex-col rounded-lg border border-gray-200 bg-white transition-shadow hover:shadow-lg'>
        <div className='relative aspect-4/3 max-h-50'>
          <Image
            alt={productName}
            className='object-contain transition-transform duration-300 group-hover:scale-105'
            fill
            sizes='(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw'
            src={imageUrl}
          />
        </div>

        <div className='z-50 flex flex-col gap-2 border-gray-200 border-t bg-white p-4'>
          <h3
            className='line-clamp-2 min-h-10 font-normal text-gray-700 text-sm'
            title={productName}
          >
            {productName}
          </h3>

          {hasRating && (
            <div className='flex items-center gap-1.5'>
              <span className='font-semibold text-gray-900 text-sm'>
                {rating.toFixed(1)}
              </span>
              <div className='flex gap-0.5'>
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star
                    className='h-3 w-3 fill-yellow-400 text-yellow-400'
                    // biome-ignore lint/suspicious/noArrayIndexKey: < !-- Index is acceptable here as the list is static and will not change -->
                    key={index}
                  />
                ))}
              </div>
              <span className='text-gray-500 text-xs'>({ratingCount})</span>
            </div>
          )}

          <div className='mt-auto space-y-1'>
            {hasDiscount && (
              <div className='flex items-center gap-2'>
                <span className='rounded bg-green-50 px-2 py-0.5 font-bold text-[10px] text-green-600 uppercase'>
                  {discountPercentage}% OFF
                </span>
                <span className='text-gray-400 text-xs line-through'>
                  R$ {formatCurrency(regularPrice)}
                </span>
              </div>
            )}

            <p className='font-bold text-2xl text-gray-900'>
              R${formatCurrency(displayPrice)}
            </p>

            <p className='text-gray-600 text-xs'>
              ou {INSTALLMENTS_COUNT}x de R${" "}
              {formatCurrency(displayPrice / INSTALLMENTS_COUNT)} sem juros
            </p>
          </div>
        </div>
      </article>
    </Link>
  )
}
